import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import { TechshowPortalContent } from "@app/types/ServerCustomContext";
import MetaData from "@components/Modules/MetaData";
import {
  mapDataToMetaData,
  mapErrorDataToMetaData,
} from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import type { LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";

import { fetchTechshowData } from "./TechshowPortal.server";
import TechshowPortalLayout from "./TechshowPortalLayout";
import TechshowPortalListingLayout from "./TechshowPortalListingLayout";
import TechshowPortalViewLayout from "./TechshowPortalViewLayout";
import { buildCanonicalPath } from "./utils";

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<TechshowPortalContent, string>;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  const payload = loaderData.payload;
  const metaData = mapDataToMetaData(payload, "WebPage");
  const {
    brochureAction,
    brochureViewId,
    filterCategorySelected,
    filterBrandSelected,
  } = payload;

  const canonicalUrl = buildCanonicalPath({
    basePath: metaData.path,
    brochureAction,
    brochureViewId,
    filterCategorySelected,
    filterBrandSelected,
  });

  return MetaData({
    ...metaData,
    path: canonicalUrl,
    canonicalUrl,
  });
};

export default function TechshowPortalPage() {
  const response: TRouteWithRedirect<TechshowPortalContent, string> =
    useLoaderData();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const {
    brochureAction = "default",
    brochureImages,
    brochureNext,
    brochurePrev,
    brochureViewId,
    displaySetting,
    filterBrands,
    filterBrandSelected,
    filterCategories,
    filterCategorySelected,
    filterMap,
    listedStories,
    mainBodyElements,
    // ogFields,
    path,
    processedElements,
    // seoFields,
    sponsorImages,
    sponsorImagesLabel,
    tags,
    techPortalFields,
    techshowBrochureSections,
    techshowInfos,
    topStories,
  } = response.payload;

  const { coverImage, featuredImage, headline, standfirst } = processedElements;

  // const title = seoFields.metaTitle || headline;
  // const description = seoFields.metaDescription;
  // const robots =
  //   seoFields.metaRobots === "1"
  //     ? "noindex, nofollow"
  //     : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  const canonicalPath = buildCanonicalPath({
    basePath: path,
    brochureAction,
    brochureViewId,
    filterCategorySelected,
    filterBrandSelected,
  });

  const layoutProps = {
    brochureImages,
    brochureNext,
    brochurePrev,
    coverImage,
    displaySetting,
    featuredImage,
    filterBrands,
    filterCategories,
    filterMap,
    headline,
    listedStories,
    mainBodyElements,
    path: canonicalPath,
    selectedBrand: filterBrandSelected,
    selectedCategory: filterCategorySelected,
    sponsorImages,
    sponsorImagesLabel,
    standfirst,
    tags,
    techPortalFields,
    techshowBrochureSections,
    techshowInfos,
    topStories,
  };

  const layoutMap: Record<string, typeof TechshowPortalLayout> = {
    "view-brochure": TechshowPortalViewLayout,
    brochures: TechshowPortalListingLayout,
    default: TechshowPortalLayout,
  };

  const LayoutComponent = layoutMap[brochureAction];

  return (
    <PubBaseLayout ads={true}>
      {/* <MetaData
        title={title}
        type="WebPage"
        path={canonicalPath}
        image={coverImage?.crops.landscape}
        description={description}
        robots={robots}
        ogFields={ogFields}
      /> */}
      {LayoutComponent && <LayoutComponent {...layoutProps} />}
    </PubBaseLayout>
  );
}

export async function loader({
  request,
}: LoaderFunctionArgs): Promise<
  TRouteWithRedirect<
    TechshowPortalContent | GlobalBaseData,
    GlobalBaseData | string
  >
> {
  const data = await fetchTechshowData(request.url);

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}
